import {invalidateOnSuccess, metadaApi} from '@dms/api/base';

import {
  GetServiceItemMoveReasonsApiResponse,
  GetServiceItemMoveReasonsApiArg,
  GetServiceItemMoveReasonsApiResponseSchema,
  GetServiceItemMoveReasonsApiArgSchema,
  PostServiceItemMoveReasonApiResponse,
  PostServiceItemMoveReasonApiArg,
  PostServiceItemMoveReasonApiResponseSchema,
  PostServiceItemMoveReasonApiArgSchema,
  GetServiceItemMoveReasonApiResponse,
  GetServiceItemMoveReasonApiArg,
  GetServiceItemMoveReasonApiResponseSchema,
  GetServiceItemMoveReasonApiArgSchema,
  PatchServiceItemMoveReasonApiResponse,
  PatchServiceItemMoveReasonApiArg,
  PatchServiceItemMoveReasonApiResponseSchema,
  PatchServiceItemMoveReasonApiArgSchema,
  PatchServiceItemMoveReasonSetActiveApiResponse,
  PatchServiceItemMoveReasonSetActiveApiArg,
  PatchServiceItemMoveReasonSetActiveApiResponseSchema,
  PatchServiceItemMoveReasonSetActiveApiArgSchema,
  PatchServiceItemMoveReasonSetInactiveApiResponse,
  PatchServiceItemMoveReasonSetInactiveApiArg,
  PatchServiceItemMoveReasonSetInactiveApiResponseSchema,
  PatchServiceItemMoveReasonSetInactiveApiArgSchema,
  PatchServiceItemMoveReasonSetDefaultApiResponse,
  PatchServiceItemMoveReasonSetDefaultApiArg,
  PatchServiceItemMoveReasonSetDefaultApiResponseSchema,
  PatchServiceItemMoveReasonSetDefaultApiArgSchema,
  PatchServiceItemMoveReasonUnsetDefaultApiResponse,
  PatchServiceItemMoveReasonUnsetDefaultApiArg,
  PatchServiceItemMoveReasonUnsetDefaultApiResponseSchema,
  PatchServiceItemMoveReasonUnsetDefaultApiArgSchema,
} from './types';

const BASE_URL = '/core/v1/codelist/service-item-move-reason';

const LIST_TAG = {type: 'dictionary', id: 'serviceItemMoveReason'} as const;

export const metadaWorkshopServiceItemMoveReasonApi = metadaApi.injectEndpoints({
  endpoints: (build) => ({
    getServiceItemMoveReasons: build.query<
      GetServiceItemMoveReasonsApiResponse,
      GetServiceItemMoveReasonsApiArg
    >({
      query: () => ({
        url: BASE_URL,
      }),
      providesTags: [LIST_TAG],
      extraOptions: {
        responseSchema: GetServiceItemMoveReasonsApiResponseSchema,
        requestSchema: GetServiceItemMoveReasonsApiArgSchema,
      },
    }),
    postServiceItemMoveReason: build.mutation<
      PostServiceItemMoveReasonApiResponse,
      PostServiceItemMoveReasonApiArg
    >({
      query: (queryArg) => ({
        url: BASE_URL,
        method: 'POST',
        body: queryArg.body,
        queryArg,
      }),
      invalidatesTags: invalidateOnSuccess([LIST_TAG]),
      extraOptions: {
        responseSchema: PostServiceItemMoveReasonApiResponseSchema,
        requestSchema: PostServiceItemMoveReasonApiArgSchema,
      },
    }),
    getServiceItemMoveReason: build.query<
      GetServiceItemMoveReasonApiResponse,
      GetServiceItemMoveReasonApiArg
    >({
      query: (queryArg) => ({
        url: `${BASE_URL}/${queryArg.codelistId}`,
        queryArg,
      }),
      providesTags: (result, error, queryArg) => [
        LIST_TAG,
        {type: 'dictionary', id: queryArg.codelistId},
      ],
      extraOptions: {
        responseSchema: GetServiceItemMoveReasonApiResponseSchema,
        requestSchema: GetServiceItemMoveReasonApiArgSchema,
      },
    }),
    patchServiceItemMoveReason: build.mutation<
      PatchServiceItemMoveReasonApiResponse,
      PatchServiceItemMoveReasonApiArg
    >({
      query: (queryArg) => ({
        url: `${BASE_URL}/${queryArg.codelistId}`,
        method: 'PATCH',
        body: queryArg.body,
        queryArg,
      }),
      invalidatesTags: invalidateOnSuccess(
        (_result, queryArg: PatchServiceItemMoveReasonApiArg) => [
          LIST_TAG,
          {type: 'dictionary', id: queryArg.codelistId},
        ]
      ),
      extraOptions: {
        responseSchema: PatchServiceItemMoveReasonApiResponseSchema,
        requestSchema: PatchServiceItemMoveReasonApiArgSchema,
      },
    }),
    patchServiceItemMoveReasonSetActive: build.mutation<
      PatchServiceItemMoveReasonSetActiveApiResponse,
      PatchServiceItemMoveReasonSetActiveApiArg
    >({
      query: (queryArg) => ({
        url: `${BASE_URL}/${queryArg.codelistId}/set-active`,
        method: 'PATCH',
        queryArg,
      }),
      invalidatesTags: invalidateOnSuccess(
        (_result, queryArg: PatchServiceItemMoveReasonSetActiveApiArg) => [
          LIST_TAG,
          {type: 'dictionary', id: queryArg.codelistId},
        ]
      ),
      extraOptions: {
        responseSchema: PatchServiceItemMoveReasonSetActiveApiResponseSchema,
        requestSchema: PatchServiceItemMoveReasonSetActiveApiArgSchema,
      },
    }),
    patchServiceItemMoveReasonSetInactive: build.mutation<
      PatchServiceItemMoveReasonSetInactiveApiResponse,
      PatchServiceItemMoveReasonSetInactiveApiArg
    >({
      query: (queryArg) => ({
        url: `${BASE_URL}/${queryArg.codelistId}/set-inactive`,
        method: 'PATCH',
        queryArg,
      }),
      invalidatesTags: invalidateOnSuccess(
        (_result, queryArg: PatchServiceItemMoveReasonSetInactiveApiArg) => [
          LIST_TAG,
          {type: 'dictionary', id: queryArg.codelistId},
        ]
      ),
      extraOptions: {
        responseSchema: PatchServiceItemMoveReasonSetInactiveApiResponseSchema,
        requestSchema: PatchServiceItemMoveReasonSetInactiveApiArgSchema,
      },
    }),
    patchServiceItemMoveReasonSetDefault: build.mutation<
      PatchServiceItemMoveReasonSetDefaultApiResponse,
      PatchServiceItemMoveReasonSetDefaultApiArg
    >({
      query: (queryArg) => ({
        url: `${BASE_URL}/${queryArg.codelistId}/set-default`,
        method: 'PATCH',
        queryArg,
      }),
      invalidatesTags: invalidateOnSuccess(
        (_result, queryArg: PatchServiceItemMoveReasonSetDefaultApiArg) => [
          LIST_TAG,
          {type: 'dictionary', id: queryArg.codelistId},
        ]
      ),
      extraOptions: {
        responseSchema: PatchServiceItemMoveReasonSetDefaultApiResponseSchema,
        requestSchema: PatchServiceItemMoveReasonSetDefaultApiArgSchema,
      },
    }),
    patchServiceItemMoveReasonUnsetDefault: build.mutation<
      PatchServiceItemMoveReasonUnsetDefaultApiResponse,
      PatchServiceItemMoveReasonUnsetDefaultApiArg
    >({
      query: (queryArg) => ({
        url: `${BASE_URL}/${queryArg.codelistId}/unset-default`,
        method: 'PATCH',
        queryArg,
      }),
      invalidatesTags: invalidateOnSuccess(
        (_result, queryArg: PatchServiceItemMoveReasonUnsetDefaultApiArg) => [
          LIST_TAG,
          {type: 'dictionary', id: queryArg.codelistId},
        ]
      ),
      extraOptions: {
        responseSchema: PatchServiceItemMoveReasonUnsetDefaultApiResponseSchema,
        requestSchema: PatchServiceItemMoveReasonUnsetDefaultApiArgSchema,
      },
    }),
  }),
});

export const {
  useGetServiceItemMoveReasonsQuery,
  usePostServiceItemMoveReasonMutation,
  useGetServiceItemMoveReasonQuery,
  usePatchServiceItemMoveReasonMutation,
  usePatchServiceItemMoveReasonSetActiveMutation,
  usePatchServiceItemMoveReasonSetInactiveMutation,
  usePatchServiceItemMoveReasonSetDefaultMutation,
  usePatchServiceItemMoveReasonUnsetDefaultMutation,
} = metadaWorkshopServiceItemMoveReasonApi;
